import { ReactNode } from 'react';
import { Badge, Flex, Text } from '@chakra-ui/react';

interface CurWizardHeaderProps {
  /** 1 = Curriculum info, 2 = Plan structure. */
  step: 1 | 2;
  /** Title beside the Admin chip — the wizard name on step 1, the curriculum's on step 2. */
  title: string;
  /** Buttons pinned to the right of the header row. */
  actions?: ReactNode;
}

const STEP_LABELS = ['Curriculum info', 'Plan structure'] as const;

// Shared header for the two-step create-curriculum wizard: Admin chip, title,
// and the step indicator. Both steps render this so the numbering and copy stay
// in one place.
export default function CurWizardHeader({ step, title, actions }: CurWizardHeaderProps) {
  return (
    <Flex align='center' gap={3} flexWrap='wrap' pb={3} borderBottomWidth='1px' borderColor='border'>
      <Badge bg='slot4.bg' color='slot4.fg' size='sm'>
        Admin
      </Badge>
      <Text fontSize='md' fontWeight='600' color='fg.strong'>
        {title}
      </Text>

      <Flex align='center' gap={2}>
        {STEP_LABELS.map((label, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === step;
          return (
            <Flex key={label} align='center' gap={2}>
              {index > 0 && <Flex w={4} h='1px' bg='border.strong' />}
              <Flex align='center' gap={1.5}>
                <Flex
                  align='center'
                  justify='center'
                  w='18px'
                  h='18px'
                  borderRadius='full'
                  flexShrink={0}
                  fontSize='2xs'
                  fontWeight='700'
                  bg={isActive ? 'brand.solid' : 'bg.subtle'}
                  color={isActive ? 'brand.contrast' : 'fg.subtle'}
                >
                  {stepNumber}
                </Flex>
                <Text textStyle='eyebrow' color={isActive ? 'brand.solid' : 'fg.subtle'}>
                  {label}
                </Text>
              </Flex>
            </Flex>
          );
        })}
      </Flex>

      {actions && (
        <Flex align='center' gap={2} ml='auto'>
          {actions}
        </Flex>
      )}
    </Flex>
  );
}
